import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = process.env.REACT_APP_API_URL || '';

const SUGGESTIONS = [
  "Show today's signal context",
  'Show my portfolio',
  'Analyze NVDA signals',
  'Show market fear & greed',
  'Show my order history',
  'Explain AAPL composite score',
];

export default function ChatPage() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hey! I\'m your AI market analysis assistant. I can explain signals and portfolio context. You make the final trading decision before any order is submitted.', ts: Date.now() },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: msg, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });

      const contentType = res.headers.get('content-type') || '';

      if (contentType.includes('text/event-stream')) {
        // SSE stream from LLM
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';
        let meta = { source: 'llm', cost: 0 };

        // Add empty assistant message to fill in
        const assistantIdx = messages.length + 1; // +1 for userMsg
        setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true, ts: Date.now() }]);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'token') {
                fullText += event.text;
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.streaming) {
                    updated[updated.length - 1] = { ...last, content: fullText };
                  }
                  return updated;
                });
              } else if (event.type === 'done') {
                meta = { source: event.source || 'llm', cost: event.token_cost || 0 };
              } else if (event.type === 'error') {
                fullText += `\nError: ${event.text}`;
              }
            } catch { /* skip malformed */ }
          }
        }

        // Finalize message
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.streaming) {
            updated[updated.length - 1] = { ...last, content: fullText, streaming: false, source: meta.source, cost: meta.cost };
          }
          return updated;
        });

      } else {
        // Regular JSON response (data queries)
        const data = await res.json();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          source: data.source,
          cost: data.token_cost,
          ts: Date.now(),
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant', content: `Error: ${err.message}`, ts: Date.now(),
      }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.h1}>AI Market Analysis Assistant</h1>
        <p style={s.sub}>Ask about signal context, portfolio, or market analysis · <span style={{color:'var(--t3)', fontSize: 11}}>Prices reflect latest closing data (swing trading)</span></p>
      </div>

      <div style={s.chatArea}>
        <div style={s.messages}>
          {messages.map((m, i) => (
            <div key={i} style={m.role === 'user' ? s.userRow : s.assistantRow}>
              {m.role === 'assistant' && <div style={s.avatar}>TM</div>}
              <div style={m.role === 'user' ? s.userBubble : s.assistantBubble}>
                <div style={m.role === 'user' ? s.msgText : s.mdText}>
                  {m.role === 'assistant'
                    ? <Markdown remarkPlugins={[remarkGfm]} components={mdComponents}>{formatMd(m.content)}</Markdown>
                    : m.content}
                </div>
                {m.source && (
                  <div style={s.meta}>
                    <span style={{
                      ...s.sourceBadge,
                      background: m.source === 'llm' ? 'var(--amber-bg)' : 'var(--green-bg)',
                      color: m.source === 'llm' ? 'var(--amber)' : 'var(--green)',
                    }}>
                      {m.source === 'llm' ? '🤖 AI' : m.source === 'db' ? '⚡ Instant' : '📡 API'}
                    </span>
                    {m.cost > 0 && <span style={s.cost}>${m.cost.toFixed(4)}</span>}
                  </div>
                )}
              </div>
              {m.role === 'user' && <div style={s.userAvatar}>D</div>}
            </div>
          ))}
          {loading && (
            <div style={s.assistantRow}>
              <div style={s.avatar}>TM</div>
              <div style={s.assistantBubble}>
                <div style={s.typing}>
                  <span style={s.dot1} /><span style={s.dot2} /><span style={s.dot3} />
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      <div style={s.suggestions}>
        {SUGGESTIONS.map((q, i) => (
          <button key={i} onClick={() => send(q)} style={s.sugBtn} disabled={loading}>{q}</button>
        ))}
      </div>

      <div style={s.inputArea}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ask about signal context, portfolio, or market analysis..."
          style={s.textarea}
          rows={1}
          disabled={loading}
          aria-label="Chat message input"
        />
        <button onClick={() => send()} disabled={loading || !input.trim()} style={s.sendBtn} aria-label="Send message">
          ↑
        </button>
      </div>
    </div>
  );
}

// Clean up markdown so tables render properly
function formatMd(text) {
  let out = text.replace(/\n{3,}/g, '\n\n');
  out = out.replace(/(\|[^\n]+\|)\n\n(\|)/g, '$1\n$2');
  out = out.replace(/([^|\n\r])\n(\|[^\n]*\|)/g, '$1\n\n$2');
  out = out.replace(/(\|[^\n]+\|)\n([^|\n])/g, '$1\n\n$2');
  return out;
}

const mdComponents = {
  h1: ({children}) => <h3 style={{fontSize: 14, fontWeight: 700, margin: '10px 0 4px', color: 'var(--t1)'}}>{children}</h3>,
  h2: ({children}) => <h4 style={{fontSize: 13, fontWeight: 700, margin: '10px 0 4px', color: 'var(--t1)'}}>{children}</h4>,
  h3: ({children}) => <h5 style={{fontSize: 13, fontWeight: 700, margin: '8px 0 4px', color: 'var(--t1)'}}>{children}</h5>,
  p: ({children}) => <p style={{margin: '3px 0', lineHeight: 1.5, fontSize: 13}}>{children}</p>,
  strong: ({children}) => <strong style={{fontWeight: 700, color: 'var(--green)'}}>{children}</strong>,
  ul: ({children}) => <ul style={{margin: '2px 0', paddingLeft: 16}}>{children}</ul>,
  ol: ({children}) => <ol style={{margin: '2px 0', paddingLeft: 16}}>{children}</ol>,
  li: ({children}) => <li style={{margin: 0, lineHeight: 1.5, fontSize: 13}}>{children}</li>,
  code: ({inline, children}) => inline
    ? <code style={{background: 'var(--bg-3)', padding: '1px 5px', borderRadius: 4, fontSize: 12, color: 'var(--green)'}}>{children}</code>
    : <pre style={{background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, overflowX: 'auto', fontSize: 12, margin: '4px 0'}}><code>{children}</code></pre>,
  table: ({children}) => (
    <div style={{overflowX: 'auto', margin: '6px 0', borderRadius: 8, border: '1px solid var(--border)'}}>
      <table style={{borderCollapse: 'collapse', width: '100%', fontSize: 12}}>{children}</table>
    </div>
  ),
  thead: ({children}) => <thead style={{background: 'var(--bg-3)'}}>{children}</thead>,
  th: ({children}) => (
    <th style={{
      padding: '6px 10px', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap',
      color: 'var(--t2)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
      borderBottom: '1px solid var(--border)',
    }}>{children}</th>
  ),
  td: ({children}) => {
    const text = typeof children === 'string' ? children : (children?.toString?.() || '');
    const isBuy = /STRONG BUY|BUY|FEAR BUY/i.test(text) && !/GREED/i.test(text);
    const isSell = /SELL|GREED SELL/i.test(text);
    const isWatch = /HOLD|WATCH|FEAR BUY/i.test(text);
    let color = 'var(--t1)';
    if (isBuy) color = 'var(--green)';
    else if (isSell) color = 'var(--red)';
    else if (isWatch) color = 'var(--amber)';
    return (
      <td style={{
        padding: '6px 10px', borderBottom: '1px solid var(--border-s)',
        fontSize: 12, color,
      }}>{children}</td>
    );
  },
  tr: ({children, ...props}) => (
    <tr style={{transition: 'background 0.1s'}} onMouseEnter={e => e.currentTarget.style.background='var(--bg-2)'} onMouseLeave={e => e.currentTarget.style.background='transparent'}>{children}</tr>
  ),
};

const s = {
  page: { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' },
  header: { marginBottom: 16, flexShrink: 0 },
  h1: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub: { fontSize: 13, color: 'var(--t3)' },
  chatArea: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  messages: { flex: 1, overflowY: 'auto', padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 16 },
  userRow: { display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: 10 },
  assistantRow: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  avatar: {
    width: 32, height: 32, borderRadius: 8, background: 'var(--green)',
    color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 800, flexShrink: 0,
  },
  userAvatar: {
    width: 32, height: 32, borderRadius: 8, background: 'var(--bg-3)',
    color: 'var(--t2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, flexShrink: 0,
  },
  userBubble: {
    maxWidth: '70%', padding: '12px 16px', borderRadius: '14px 14px 4px 14px',
    background: 'var(--green)', color: '#000', fontSize: 14, lineHeight: 1.5,
  },
  assistantBubble: {
    maxWidth: '85%', padding: '12px 16px', borderRadius: '14px 14px 14px 4px',
    background: 'var(--bg-1)', border: '1px solid var(--border)', fontSize: 13, lineHeight: 1.5,
  },
  msgText: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  mdText: { wordBreak: 'break-word' },
  meta: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 },
  sourceBadge: { fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4 },
  cost: { fontSize: 10, color: 'var(--t3)' },
  typing: { display: 'flex', gap: 4, padding: '4px 0' },
  dot1: { width: 6, height: 6, borderRadius: '50%', background: 'var(--t3)', animation: 'pulse 1.2s infinite 0s' },
  dot2: { width: 6, height: 6, borderRadius: '50%', background: 'var(--t3)', animation: 'pulse 1.2s infinite 0.2s' },
  dot3: { width: 6, height: 6, borderRadius: '50%', background: 'var(--t3)', animation: 'pulse 1.2s infinite 0.4s' },
  suggestions: {
    display: 'flex', flexWrap: 'wrap', gap: 6, padding: '12px 0', flexShrink: 0,
  },
  sugBtn: {
    padding: '6px 14px', background: 'var(--bg-1)', border: '1px solid var(--border)',
    borderRadius: 20, color: 'var(--t2)', fontSize: 12, cursor: 'pointer',
    transition: 'all 0.12s',
  },
  inputArea: {
    display: 'flex', gap: 10, padding: '16px 0', borderTop: '1px solid var(--border)',
    flexShrink: 0,
  },
  textarea: {
    flex: 1, padding: '12px 16px', background: 'var(--bg-1)', border: '1px solid var(--border)',
    borderRadius: 12, color: 'var(--t1)', fontSize: 14, resize: 'none', outline: 'none',
    fontFamily: 'var(--font)', lineHeight: 1.5,
  },
  sendBtn: {
    width: 44, height: 44, borderRadius: 12, background: 'var(--green)', color: '#000',
    border: 'none', fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0,
  },
};





